import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Search, Inbox } from "lucide-react";
import { listItems } from "../../lib/uploadsApi";
import { useRole } from "../../lib/role.jsx";
import { UploadNotices } from "../uploads/FileUploadSource";
import BronzeReview from "../pipeline/BronzeReview";
import { LayerChip, StatusPill, Pill, OPEN, SOURCE, when, num } from "../pipeline/Layer";
import SavedSteps from "./SavedSteps";
import LearnedDecisions from "./LearnedDecisions";
import MatchingMasters from "./MatchingMasters";
import { cn } from "../../lib/utils";

const GROUPS = [["waiting", "Waiting", (s) => OPEN.includes(s)], ["too_large", "Too large", (s) => s === "too_large"],
  ["silver", "Promoted", (s) => s === "silver"], ["rejected", "Rejected", (s) => s === "rejected"], ["all", "All", () => true]];

function Chips({ value, onChange, options, testid, counts }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid={testid}>
      {options.map(([k, l]) => (
        <button key={k} data-testid={`${testid}-${k}`} onClick={() => onChange(k)}
                className={cn("h-8 px-3 rounded-sm border text-[12px] transition-colors", value === k ? "bg-primary text-primary-fg border-primary" : "border-border hover:border-primary")}>
          {l}{counts && <span className="ml-1.5 font-mono-tabular opacity-75">{counts[k] ?? 0}</span>}</button>))}
    </div>
  );
}

function Row({ i, meta, onOpen }) {
  return (
    <tr data-testid={`bz-item-${i.id}`} onClick={() => onOpen(i.id)} className="border-b border-border/60 cursor-pointer hover:bg-surface-2/60 transition-colors">
      <td className="px-3 py-2.5"><div className="flex items-center gap-2"><LayerChip layer="Bronze" /><span className="font-medium truncate max-w-[280px]">{i.filename}{i.sheet ? ` · ${i.sheet}` : ""}</span></div>
        {i.status === "rejected" && <div className="text-[11px] text-critical mt-0.5 truncate max-w-[420px]">Rejected: {i.reject_reason}</div>}</td>
      <td className="px-3 py-2.5"><Pill tone="neutral">{SOURCE[i.source] || "Upload"}</Pill></td>
      <td className="px-3 py-2.5 text-[12px]">{meta.types[i.type]?.label || <span className="text-warning">Unrecognised</span>}</td>
      <td className="px-3 py-2.5 font-mono-tabular text-right">{num(i.profile?.rows)}</td>
      <td className="px-3 py-2.5 font-mono-tabular text-[11.5px]">{i.uploaded_by}</td>
      <td className="px-3 py-2.5 font-mono-tabular text-[11.5px] text-muted whitespace-nowrap">{when(i.uploaded_at)}</td>
      <td className="px-3 py-2.5"><div className="flex items-center gap-1.5"><StatusPill status={i.status} testid={`bz-status-${i.id}`} />
        {i.promotion?.state === "running" && <Pill tone="info" testid={`bz-promoting-${i.id}`}>Promoting…</Pill>}
        {i.silver_removed && <span title={i.silver_removed.reason}><Pill tone="warning" testid={`bz-silver-removed-${i.id}`}>Rows removed from Silver</Pill></span>}</div></td>
    </tr>
  );
}

// Console → Bronze: the single review queue for uploads and S3-staged files, plus the saved cleaning steps.
export default function BronzeTab({ meta, initialItem, onToast, onChanged }) {
  const { role } = useRole();
  const [d, setD] = useState(null);
  const [group, setGroup] = useState("waiting");
  const [source, setSource] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(initialItem || null);
  const [learnedKey, setLearnedKey] = useState(0);
  const load = useCallback(() => listItems().then(setD).catch(e => onToast(e.message)), [onToast]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (initialItem) setOpen(initialItem); }, [initialItem]);
  const items = useMemo(() => d?.items || [], [d]);
  const counts = useMemo(() => Object.fromEntries(GROUPS.map(([k, , f]) => [k, items.filter(i => f(i.status)).length])), [items]);
  const shown = items.filter(i => GROUPS.find(g => g[0] === group)[2](i.status) && (source === "all" || (i.source || "upload") === source)
    && (!q.trim() || `${i.filename} ${i.sheet} ${i.uploaded_by}`.toLowerCase().includes(q.trim().toLowerCase())));
  const done = (msg) => { setOpen(null); onToast(msg); load(); onChanged(); setLearnedKey(k => k + 1); };
  return (
    <div className="space-y-5" data-testid="tab-bronze">
      {role?.id === "it_head" && <UploadNotices notices={d?.notices} />}
      {meta?.can_review && <MatchingMasters key={learnedKey} onToast={onToast} onBuilt={() => { load(); onChanged(); }} />}
      <div className="tile p-3 flex flex-wrap items-center gap-3">
        <Chips value={group} onChange={setGroup} options={GROUPS} testid="bz-group" counts={counts} />
        <Chips value={source} onChange={setSource} options={[["all", "All sources"], ["upload", "Upload"], ["s3", "S3"]]} testid="bz-source" />
        <div className="relative flex-1 min-w-[180px]"><Search size={12} className="absolute left-2.5 top-2.5 text-muted" />
          <input data-testid="bz-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search file, sheet or person" className="w-full h-8 pl-7 pr-2 bg-surface border border-border rounded-sm text-[12px] focus:outline-none focus:border-primary" /></div>
      </div>
      <div className="tile overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="bz-queue">
            <thead><tr className="text-[10px] uppercase tracking-widest text-muted border-b border-border">
              {["Bronze item", "Source", "Table type", "Rows", "Brought in by", "Landed", "Status"].map((h, k) => <th key={h} className={cn("px-3 py-2 font-semibold", k === 3 ? "text-right" : "text-left")}>{h}</th>)}
            </tr></thead>
            <tbody>{shown.map(i => <Row key={i.id} i={i} meta={meta} onOpen={setOpen} />)}</tbody>
          </table>
        </div>
        {d && !shown.length && <div className="py-10 text-center text-[12.5px] text-muted" data-testid="bz-empty"><Inbox size={18} className="mx-auto mb-2 text-primary/60" />
          {group === "waiting" ? "Nothing is waiting in Bronze. Files arrive from Live Data Pipeline — File Upload or S3 Discover." : "No Bronze items match these filters."}</div>}
        {!d && <div className="py-10 text-center text-[12px] text-muted">Loading Bronze…</div>}
      </div>
      <SavedSteps meta={meta} onToast={onToast} />
      <LearnedDecisions key={learnedKey} meta={meta} onToast={onToast} />
      {open && <BronzeReview id={open} meta={meta} onClose={() => { setOpen(null); load(); setLearnedKey(k => k + 1); }} onDone={done} />}
    </div>
  );
}
